/**
 * PDP-08: Delivery availability — read-only shipping estimates.
 * IM-005: Address changes refresh warehouse inventory cookies from the PDP estimate (same source rules as checkout).
 * KINDW-1508: Default delivery address from browser geolocation (not preferred showroom).
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {
  Box,
  Flex,
  Skeleton,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import MobileAnchoredTooltip from '@amblique/king-living/app/components/mobile-anchored-tooltip';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import AddressAutocomplete from '@amblique/king-living/app/components/address-autocomplete/address-autocomplete';
import {getSiteConfig} from '@amblique/king-living/app/constants/site-config';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {usePdpShippingMethods} from '@amblique/king-living/app/hooks/use-pdp-shipping-methods';
import {usePdpWarehouseInventoryContext} from '@amblique/king-living/app/context/pdp-warehouse-inventory-context';
import {useWarehouseInventory} from '@amblique/king-living/app/hooks/use-warehouse-inventory';
import {formatInventoryIdsParam} from '@amblique/king-living/app/utils/warehouse-inventory-cookies';
import {useGeocoding} from '@amblique/king-living/app/hooks/use-geocoding';
import {buildSteps, isStepCompleteUtil} from '@amblique/king-living/app/hooks/use-customize-steps';
import {extractDecomposedProductIds} from '@amblique/king-living/app/utils/product-decomposition';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {isFinishedGood} from '@amblique/king-living/app/utils/product-utils';
import {geoResultToShippingAddress, resolveBrowserGeoFull} from '@amblique/king-living/app/utils/browser-geo-address';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {
  getFirstWhsCodeFromMethods,
  isShippingServiceApiSource,
} from '@amblique/king-living/app/utils/shipping-method-whs-code';

function PdpDeliveryInfoIcon() {
  const clipId = React.useId().replace(/:/g, '');

  return (
    <svg
      aria-hidden
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M10 0C15.5225 0.00022607 19.9998 4.47749 20 10C19.9998 15.5225 15.5225 19.9998 10 20C4.47749 19.9998 0.000226066 15.5225 0 10C0.000230878 4.47749 4.47749 0.000230883 10 0ZM10 1.42773C5.26647 1.42797 1.42797 5.26647 1.42773 10C1.42796 14.7335 5.26647 18.5711 10 18.5713C14.7335 18.5711 18.5711 14.7335 18.5713 10C18.5711 5.26647 14.7335 1.42796 10 1.42773ZM10 9.25C10.4141 9.25 10.7498 9.58598 10.75 10V14.2861C10.7498 14.7002 10.4141 15.0361 10 15.0361C9.58592 15.0361 9.25022 14.7002 9.25 14.2861V10C9.25023 9.58598 9.58593 9.25 10 9.25ZM10 5C10.5523 5 11 5.44772 11 6C11 6.55228 10.5523 7 10 7C9.44771 7 9 6.55228 9 6C9 5.44772 9.44771 5 10 5Z"
          fill="#8C683B"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function PdpDeliveryMethodSuccessIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#E8F4E7" height="20" rx="10" width="20" />
      <path
        d="M14.8535 7.28711L8.64746 13.4932L5.14648 9.99219L6.28711 8.85156L8.64746 11.2119L13.7129 6.14648L14.8535 7.28711Z"
        fill="#2D7831"
        stroke="#2D7831"
        strokeWidth="0.5"
      />
    </svg>
  );
}

/** Error state — 20×20, Error-BG + error red (matches theme `error`) */
function PdpDeliveryMethodErrorIcon() {
  const maskId = React.useId().replace(/:/g, '');

  return (
    <svg
      aria-hidden
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#FEF2F2" height="20" rx="10" width="20" />
      <mask
        fill="black"
        height="10"
        id={maskId}
        maskUnits="userSpaceOnUse"
        width="10"
        x="5"
        y="5"
      >
        <rect fill="white" height="10" width="10" x="5" y="5" />
        <path d="M14 6.72656L10.7266 9.99902L14 13.2725L13.2725 14L9.99902 10.7266L6.72754 13.999L6.00098 13.2725L9.27246 10L6 6.72754L6.72754 6L10 9.27246L13.2734 6L14 6.72656Z" />
      </mask>
      <path
        d="M14 6.72656L10.7266 9.99902L14 13.2725L13.2725 14L9.99902 10.7266L6.72754 13.999L6.00098 13.2725L9.27246 10L6 6.72754L6.72754 6L10 9.27246L13.2734 6L14 6.72656Z"
        fill="#9D2A1E"
      />
      <path
        d="M14 6.72656L14.3535 7.08017L14.7072 6.72662L14.3536 6.37301L14 6.72656ZM10.7266 9.99902L10.3731 9.64542L10.0194 9.99897L10.373 10.3526L10.7266 9.99902ZM14 13.2725L14.3536 13.626L14.7071 13.2725L14.3536 12.9189L14 13.2725ZM13.2725 14L12.9189 14.3536L13.2725 14.7071L13.626 14.3536L13.2725 14ZM9.99902 10.7266L10.3526 10.373L9.99897 10.0194L9.64542 10.3731L9.99902 10.7266ZM6.72754 13.999L6.37399 14.3526L6.72759 14.7062L7.08115 14.3525L6.72754 13.999ZM6.00098 13.2725L5.64737 12.919L5.29392 13.2725L5.64742 13.626L6.00098 13.2725ZM9.27246 10L9.62607 10.3535L9.97952 9.99995L9.62601 9.64645L9.27246 10ZM6 6.72754L5.64645 6.37399L5.29289 6.72754L5.64645 7.08109L6 6.72754ZM6.72754 6L7.08109 5.64645L6.72754 5.29289L6.37399 5.64645L6.72754 6ZM10 9.27246L9.64645 9.62601L9.99995 9.97952L10.3535 9.62607L10 9.27246ZM13.2734 6L13.627 5.64645L13.2735 5.29295L12.9199 5.64639L13.2734 6ZM14 6.72656L13.6465 6.37296L10.3731 9.64542L10.7266 9.99902L11.0801 10.3526L14.3535 7.08017L14 6.72656ZM10.7266 9.99902L10.373 10.3526L13.6464 13.626L14 13.2725L14.3536 12.9189L11.0801 9.64547L10.7266 9.99902ZM14 13.2725L13.6464 12.9189L12.9189 13.6464L13.2725 14L13.626 14.3536L14.3536 13.626L14 13.2725ZM13.2725 14L13.626 13.6464L10.3526 10.373L9.99902 10.7266L9.64547 11.0801L12.9189 14.3536L13.2725 14ZM9.99902 10.7266L9.64542 10.3731L6.37393 13.6455L6.72754 13.999L7.08115 14.3525L10.3526 11.0801L9.99902 10.7266ZM6.72754 13.999L7.08109 13.6455L6.35453 12.9189L6.00098 13.2725L5.64742 13.626L6.37399 14.3526L6.72754 13.999ZM6.00098 13.2725L6.35458 13.626L9.62607 10.3535L9.27246 10L8.91885 9.6465L5.64737 12.919L6.00098 13.2725ZM9.27246 10L9.62601 9.64645L6.35355 6.37399L6 6.72754L5.64645 7.08109L8.91891 10.3536L9.27246 10ZM6 6.72754L6.35355 7.08109L7.08109 6.35355L6.72754 6L6.37399 5.64645L5.64645 6.37399L6 6.72754ZM6.72754 6L6.37399 6.35355L9.64645 9.62601L10 9.27246L10.3536 8.91891L7.08109 5.64645L6.72754 6ZM10 9.27246L10.3535 9.62607L13.6269 6.35361L13.2734 6L12.9199 5.64639L9.6465 8.91885L10 9.27246ZM13.2734 6L12.9199 6.35355L13.6464 7.08012L14 6.72656L14.3536 6.37301L13.627 5.64645L13.2734 6Z"
        fill="#9D2A1E"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

function PdpDeliverySearchIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="20"
      viewBox="0 0 21 21"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.9792 7.15773C13.6909 4.12692 10.1891 2.71441 7.15818 4.00254C4.12751 5.2908 2.71479 8.79182 4.00261 11.8227C5.29073 14.8536 8.79214 16.267 11.8231 14.9791C14.8542 13.691 16.2674 10.1888 14.9792 7.15773ZM16.3597 6.57103C17.9719 10.3645 16.2033 14.7474 12.4098 16.3596C8.6164 17.9715 4.23425 16.2027 2.62211 12.4094C1.01026 8.61609 2.77838 4.23432 6.57148 2.62203C10.3649 1.00989 14.7474 2.77779 16.3597 6.57103Z"
        fill="#8C683B"
      />
      <path
        d="M13.7079 13.707C14.0008 13.4141 14.4756 13.4141 14.7685 13.707L19.9755 18.914L20.0263 18.9716C20.2664 19.2661 20.2497 19.6999 19.9755 19.9745C19.7009 20.2491 19.2662 20.2666 18.9716 20.0263L18.915 19.9745L13.7079 14.7675C13.4151 14.4746 13.4151 13.9998 13.7079 13.707Z"
        fill="#8C683B"
      />
    </svg>
  );
}

function formatLocationLine(address) {
  if (!address) return '';

  const city = address.city?.trim();
  const state = (address.stateCode || address.state || '').trim();
  const pc = (address.postcode || address.postalCode || '').trim();
  const locality = [city, state].filter(Boolean).join(', ');

  return [locality, pc].filter(Boolean).join(' ').trim();
}

/**
 * Compare PDP shipping response echo to the address the user selected (guards stale RQ data / race).
 */
function shippingResponseMatchesSelection(shippingAddress, responsePayload) {
  const echo = responsePayload?.requestedDeliveryAddress;

  if (!echo || !shippingAddress) {
    return true;
  }

  const pcSel = String(shippingAddress.postcode || shippingAddress.postalCode || '').replace(/\s/g, '');
  const stSel = String(shippingAddress.stateCode || shippingAddress.state || '')
    .trim()
    .toUpperCase();
  const pcEcho = String(echo.postcode || echo.postalCode || '').replace(/\s/g, '');
  const stEcho = String(echo.stateCode || echo.state || '')
    .trim()
    .toUpperCase();

  return pcSel === pcEcho && stSel === stEcho;
}

/**
 * When this block's height changes after shipping copy loads, `scrollY` stays fixed and content
 * below (Specifications) appears to jump. We scroll by the same Δh when the block is already
 * above the viewport. Uses `offsetHeight` + rAF (contentRect alone often misses text reflow) and
 * resets baseline while the methods skeleton is showing.
 *
 * @param {React.RefObject<HTMLElement | null>} rootRef
 * @param {boolean} showMethodsSkeleton
 */
function useCompensateScrollWhenHeightChangesAboveViewport(rootRef, showMethodsSkeleton) {
  const lastHeightRef = useRef(null);
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    if (showMethodsSkeleton) {
      lastHeightRef.current = null;
    }
  }, [showMethodsSkeleton]);

  useLayoutEffect(() => {
    const el = rootRef.current;

    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const flush = () => {
      rafRef.current = null;

      if (!el.isConnected || typeof window === 'undefined') return;

      const nextH = el.offsetHeight;
      const prev = lastHeightRef.current;

      lastHeightRef.current = nextH;

      if (prev == null) return;

      const delta = Math.round(nextH - prev);

      if (Math.abs(delta) < 1) return;

      const {bottom} = el.getBoundingClientRect();
      // Fully above viewport (tolerance for subpixel / iOS UI chrome)
      if (bottom > 6) return;

      window.scrollBy({left: 0, top: delta, behavior: 'auto'});
    };

    const scheduleFlush = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(flush);
      });
    };

    const ro = new ResizeObserver(scheduleFlush);

    ro.observe(el);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }

      ro.disconnect();
    };
  }, [rootRef, showMethodsSkeleton]);
}

/**
 * Determines if product customization is complete or not required.
 *
 * Returns true when:
 * - Product has no customization steps
 * - Master product with no orderable variants
 * - Only has optional add-ons (no required selections)
 * - All required customization steps are complete
 *
 * @param {Object} product - SFCC product object
 * @param {Array} steps - Customize steps from buildSteps
 * @param {Object} committedSelection - Current committed selections
 * @returns {boolean} True if delivery section should be enabled
 */
function isCustomizationCompleteOrNotRequired(product, steps, committedSelection) {
  if (!product || steps.length === 0) {
    return true;
  }

  // Finished Goods have all options pre-selected; no user customization needed.
  // committedSelection is never written for read-only Finished Good SKUs, so the
  // per-step selection check below would always return false without this guard.
  if (isFinishedGood(product)) {
    return true;
  }

  const isVariationMaster = product.type?.master === true;
  const hasOrderableVariants =
    product.variants?.length > 0 && product.variants.some((v) => v.orderable === true);

  const needsCoverSelection = isVariationMaster && hasOrderableVariants;

  const hasRequiredOptions = steps.some((s) => {
    if (s.id === 'cover' || !s.container || s.container.isVisible === false) {
      return false;
    }

    const requiredValues = s.container.values?.filter((v) => v.isRequired);

    return requiredValues && requiredValues.length > 0;
  });

  if (!needsCoverSelection && !hasRequiredOptions) {
    return true;
  }

  const selections = committedSelection?.selections;

  if (!selections || Object.keys(selections).length === 0) {
    return false;
  }

  const stepsToCheck = steps.filter((s) => {
    if (s.id === 'cover' && !needsCoverSelection) {
      return false;
    }

    return true;
  });

  return stepsToCheck.every((s) => isStepCompleteUtil(s.id, selections, steps));
}

/**
 * @param {Object} props
 * @param {Object} props.product
 * @param {string} [props.tooltipText] - Rich text not supported; plain string from Contentful. Empty hides tooltip.
 */
export function PdpDeliveryAvailability({product, tooltipText = ''}) {
  const intl = useIntl();
  const locationLabelId = React.useId();
  const rootRef = useRef(null);
  const styles = useMultiStyleConfig('PdpDeliveryAvailability', {});
  const isMobile = useBreakpointValue({base: true, lg: false});
  const {
    isOpen: isDeliveryTooltipOpen,
    onClose: onDeliveryTooltipClose,
    onToggle: onDeliveryTooltipToggle,
  } = useDisclosure();

  const {currency: activeCurrency} = useCurrency();
  const {site} = useMultiSite();
  const {committedSelection, setProductsMap} = useCustomizeJourney();

  const siteCountryCode = getSiteConfig(site?.id)?.countryCode ?? 'AU';
  const geoApiKey = site?.preferences?.GOOGLE_GEOLOCATION_API_KEY;

  const [shippingAddress, setShippingAddress] = useState(null);
  const [locationBarVisible, setLocationBarVisible] = useState(true);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isResolvingGeoAddress, setIsResolvingGeoAddress] = useState(false);
  // KINDW-1598: only persist warehouse cookies after an explicit shopper address change.
  const [persistWarehouseCookies, setPersistWarehouseCookies] = useState(false);
  const {setPdpWarehouseInventoryState} = usePdpWarehouseInventoryContext();
  const {reverseGeocode} = useGeocoding();
  const reverseGeocodeRef = useRef(reverseGeocode);
  const geoAttemptedRef = useRef(false);
  const shippingAddressSetByUserRef = useRef(false);

  useEffect(() => {
    reverseGeocodeRef.current = reverseGeocode;
  }, [reverseGeocode]);

  useEffect(() => {
    geoAttemptedRef.current = false;
    shippingAddressSetByUserRef.current = false;
  }, [site?.id]);

  const steps = useMemo(() => {
    if (!product) return [];

    if (committedSelection?.mode === 'set' && committedSelection?.productId) {
      const linkedProduct = setProductsMap?.[committedSelection.productId];

      if (linkedProduct) return buildSteps(product);
    }

    return buildSteps(product);
  }, [product, committedSelection?.mode, committedSelection?.productId, setProductsMap]);

  const isCustomizationComplete = useMemo(
    () => isCustomizationCompleteOrNotRequired(product, steps, committedSelection),
    [product, steps, committedSelection]
  );

  const decomposedIds = useMemo(
    () => extractDecomposedProductIds(product, committedSelection?.selections || {}),
    [product, committedSelection?.selections]
  );

  // SFCC-fallback lead time uses ONLY the selected variant (the chosen cover variant, or — when a
  // predefined set is open — the set's cover variant from the linked product), or the PDP product
  // itself when there is no variant selection. Option / recommendation products are treated as
  // components of that variant and never contribute their own lead time (requirement: the lead-time
  // product is the variant, not the options). API mode ignores this and respects the API result.
  const productLeadTime = useMemo(() => {
    const coverSel = committedSelection?.selections?.cover;
    const coverId = coverSel?.variantId ?? coverSel?.productId ?? null;

    // The selected cover is a variation of the PDP master product, so resolve it from the master's
    // c_allVariantsData first. In set mode, the linked set product is tried as a fallback (covers the
    // case where the cover belongs to the linked set product rather than the master).
    const setProduct =
      committedSelection?.mode === 'set' && committedSelection?.productId
        ? setProductsMap?.[committedSelection.productId] ?? null
        : null;

    const variant =
      (coverId ? getSelectedVariantProduct(product, coverId) : null) ??
      (coverId && setProduct ? getSelectedVariantProduct(setProduct, coverId) : null);

    // Resolution order: selected variant's own lead time (c_allVariantsData `leadTime`, which already
    // reflects SFCC master→variant inheritance) → master `c_leadTime` → linked set product `c_leadTime`.
    const raw =
      variant?.leadTime ??
      product?.c_leadTime ??
      setProduct?.c_leadTime;
    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [committedSelection?.mode, committedSelection?.productId, committedSelection?.selections?.cover, setProductsMap, product]);

  const cartItems = useMemo(() => {
    if (!decomposedIds.length) return [];

    return [
      {
        productId: decomposedIds[0],
        productIds: decomposedIds,
        quantity: 1,
        leadTime: productLeadTime,
      },
    ];
  }, [decomposedIds, productLeadTime]);

  useEffect(() => {
    if (!isCustomizationComplete || typeof window === 'undefined') {
      return undefined;
    }

    if (geoAttemptedRef.current) {
      return undefined;
    }

    geoAttemptedRef.current = true;

    if (!geoApiKey) {
      return undefined;
    }

    let cancelled = false;

    setIsResolvingGeoAddress(true);

    (async () => {
      try {
        const geo = await resolveBrowserGeoFull({
          siteId: site?.id,
          apiKey: geoApiKey,
          reverseGeocode: reverseGeocodeRef.current,
        });

        if (cancelled || shippingAddressSetByUserRef.current) {
          return;
        }

        const next = geoResultToShippingAddress(geo, siteCountryCode);

        if (next) {
          setShippingAddress(next);
          setLocationBarVisible(true);
          setIsEditingLocation(false);
        }
      } catch {
        // Leave address empty — shopper must enter delivery address manually.
      } finally {
        if (!cancelled) {
          setIsResolvingGeoAddress(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geoApiKey, isCustomizationComplete, site?.id, siteCountryCode]);

  const pdpQuery = usePdpShippingMethods({
    enabled: isCustomizationComplete,
    address: shippingAddress,
    cartItems,
  });

  const canRunShippingQuery =
    isCustomizationComplete &&
    Boolean(shippingAddress?.postcode && shippingAddress?.stateCode);

  const shippingPayloadMatchesSelection = shippingResponseMatchesSelection(
    shippingAddress,
    pdpQuery.data
  );

  /** Stale success: RQ can keep prior `data` while a new address fetch is in flight (v4 refetch / ordering). */
  const staleShippingPayload =
    Boolean(pdpQuery.data?.requestedDeliveryAddress) && !shippingPayloadMatchesSelection;

  /** Prefer `isFetching && !data` over `isLoading` — RQ v4 can leave `isLoading` true in some idle/pending edge cases. */
  const showMethodsSkeleton = Boolean(
    (canRunShippingQuery && pdpQuery.isFetching && pdpQuery.data === undefined) ||
    (canRunShippingQuery && staleShippingPayload && pdpQuery.isFetching)
  );

  useCompensateScrollWhenHeightChangesAboveViewport(rootRef, showMethodsSkeleton);

  const methods =
    shippingPayloadMatchesSelection ? pdpQuery.data?.methods || [] : [];
  const displayCurrency =
    methods[0]?.price?.currency || activeCurrency || product?.currency || 'AUD';

  const firstWhsCode = useMemo(() => getFirstWhsCodeFromMethods(methods), [methods]);

  const shippingApiActive = Boolean(
    shippingPayloadMatchesSelection &&
    isShippingServiceApiSource(pdpQuery.data?.source)
  );

  const warehouseInventoryAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }

    return {
      postalCode: shippingAddress.postcode || shippingAddress.postalCode || '',
      stateCode: shippingAddress.stateCode || '',
      countryCode: shippingAddress.countryCode || '',
    };
  }, [shippingAddress]);

  // Warehouse inventory (WhsCode → inventory list id) only applies on the Shipping API path.
  // SFCC-only estimates have no WhsCode; do not wait for inventoryListId in that case (KINDW-1785).
  const warehouseInventoryEnabled =
    isCustomizationComplete &&
    canRunShippingQuery &&
    shippingPayloadMatchesSelection &&
    !pdpQuery.isError &&
    Boolean(pdpQuery.data) &&
    shippingApiActive &&
    Boolean(firstWhsCode);

  // Active while PDP has a delivery address — blocks checkout cookie fallback during shipping fetch.
  const pdpInventoryContextActive = isCustomizationComplete && canRunShippingQuery;

  const {inventoryListId, isLoading: isWarehouseInventoryLoading} = useWarehouseInventory({
    whsCode: firstWhsCode,
    address: warehouseInventoryAddress,
    shippingApiActive,
    enabled: warehouseInventoryEnabled,
    persistCookies: persistWarehouseCookies,
  });

  const isPdpInventoryResolving =
    pdpInventoryContextActive &&
    (
      pdpQuery.isFetching ||
      staleShippingPayload ||
      isWarehouseInventoryLoading ||
      (warehouseInventoryEnabled && !inventoryListId)
    );

  useEffect(() => {
    const inventoryIds =
      warehouseInventoryEnabled && inventoryListId ?
        formatInventoryIdsParam(inventoryListId) :
        undefined;

    setPdpWarehouseInventoryState({
      inventoryIds,
      isActive: pdpInventoryContextActive,
      isResolving: isPdpInventoryResolving,
    });
  }, [
    inventoryListId,
    isPdpInventoryResolving,
    pdpInventoryContextActive,
    setPdpWarehouseInventoryState,
    warehouseInventoryEnabled,
  ]);

  useEffect(
    () => () => {
      setPdpWarehouseInventoryState({
        inventoryIds: undefined,
        isActive: false,
        isResolving: false,
      });
    },
    [setPdpWarehouseInventoryState]
  );

  const showSummary = Boolean(locationBarVisible && shippingAddress);

  const handleChangeLocation = useCallback(() => {
    setIsEditingLocation(true);
  }, []);

  const handleAddressSelect = useCallback(
    (parsedAddress) => {
      const pc = (parsedAddress.postalCode || '').trim();
      const st = (parsedAddress.stateCode || '').trim().toUpperCase();
      const city = (parsedAddress.city || '').trim() || null;
      const cc = (parsedAddress.countryCode || siteCountryCode).trim().toUpperCase();

      if (!pc || !st) return;

      shippingAddressSetByUserRef.current = true;
      setShippingAddress({postcode: pc, stateCode: st, city, countryCode: cc});
      setLocationBarVisible(true);
      setIsEditingLocation(false);
      setPersistWarehouseCookies(true);
    },
    [siteCountryCode]
  );

  const trimmedTooltip = typeof tooltipText === 'string' ? tooltipText.trim() : '';
  const tooltipAriaLabel = intl.formatMessage({
    defaultMessage: 'Delivery information',
    id: 'pdp_delivery.tooltip.aria_label',
  });
  const shouldShowNoMethodsState =
    !showMethodsSkeleton &&
    !pdpQuery.isError &&
    isCustomizationComplete &&
    shippingAddress &&
    methods.length === 0;
  const showDeliveryTimingDisclaimer =
    methods.length > 0 && !showMethodsSkeleton && !pdpQuery.isError;

  return (
    <Box
      ref={rootRef}
      className={`pdp-delivery-availability ${isCustomizationComplete ? '' : 'opacity-50'}`}
      {...styles.root}
      pointerEvents={isCustomizationComplete ? 'auto' : 'none'}
    >
      <Flex {...styles.titleRow}>
        <Text className={isCustomizationComplete ? '' : 'text-greyscale-70'} {...styles.title}>
          <FormattedMessage
            defaultMessage="Delivery availability"
            id="pdp_delivery.title"
          />
        </Text>
        {trimmedTooltip ?
          isMobile ?
            (
              <MobileAnchoredTooltip
                isOpen={isDeliveryTooltipOpen}
                label={trimmedTooltip}
                interactive
                onClose={onDeliveryTooltipClose}
                onToggle={onDeliveryTooltipToggle}
              >
                <Box
                  aria-label={tooltipAriaLabel}
                  as="button"
                  type="button"
                  {...styles.titleTooltipButton}
                >
                  <PdpDeliveryInfoIcon />
                </Box>
              </MobileAnchoredTooltip>
            ) :
            (
              <Tooltip hasArrow label={trimmedTooltip} placement="top">
                <Box
                  aria-label={tooltipAriaLabel}
                  as="button"
                  type="button"
                  {...styles.titleTooltipButton}
                >
                  <PdpDeliveryInfoIcon />
                </Box>
              </Tooltip>
            ) :
          null}
      </Flex>

      <Box {...styles.card} aria-busy={showMethodsSkeleton}>
        <Box className="location-section" {...styles.locationSection}>
          <Text id={locationLabelId} className={isCustomizationComplete ? '' : 'text-greyscale-65'} {...styles.locationLabel}>
            <FormattedMessage
              defaultMessage="Enter delivery address:"
              id="pdp_delivery.location.label"
            />
          </Text>

          {isResolvingGeoAddress && !shippingAddress && !isEditingLocation ? (
            <Skeleton borderRadius="md" height="40px" width="100%" />
          ) : showSummary && !isEditingLocation ? (
            <Flex {...styles.locationAddressRow}>
              <Box {...styles.locationSearchIconWrap} aria-hidden>
                <PdpDeliverySearchIcon />
              </Box>
              <Text {...styles.locationSummary}>{formatLocationLine(shippingAddress)}</Text>
              <Text
                {...styles.changeLink}
                as="button"
                type="button"
                onClick={handleChangeLocation}
              >
                <FormattedMessage defaultMessage="Change" id="pdp_delivery.location.change" />
              </Text>
            </Flex>
          ) : (
            <Flex {...styles.searchRow}>
              <Flex {...styles.locationInputsGroup}>
                <AddressAutocomplete
                  ariaLabelledBy={locationLabelId}
                  clearButtonProps={styles.locationAutocompleteClear}
                  countryCode={siteCountryCode.toLowerCase()}
                  flex={1}
                  inputId={`${locationLabelId}-input`}
                  inputProps={{...styles.locationAutocompleteInput, autoFocus: isEditingLocation}}
                  inputRowProps={styles.locationAutocompleteRow}
                  minW={0}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Search for your address',
                    id: 'pdp_delivery.location.search_placeholder',
                  })}
                  showClearButton
                  startElement={
                    <Box {...styles.locationAutocompleteIconWrap}>
                      <PdpDeliverySearchIcon />
                    </Box>
                  }
                  uiVariant="pdp"
                  onAddressSelect={handleAddressSelect}
                />
              </Flex>
            </Flex>
          )}
        </Box>

        <Flex
          className="methods-stack"
          minH={showMethodsSkeleton ? '168px' : undefined}
          {...styles.methodsStack}
        >
        {showMethodsSkeleton &&
          [1, 2].map((i) => (
            <Box key={i} {...styles.methodRow}>
              <Skeleton borderRadius="full" flexShrink={0} height="20px" width="20px" />
              <Box flex={1}>
                <Skeleton height="14px" marginBottom={2} width="40%" />
                <Skeleton height="12px" width="70%" />
              </Box>
              <Skeleton height="14px" width="48px" />
            </Box>
          ))}

        {!showMethodsSkeleton && pdpQuery.isError && (
          <Text color="red.600" fontSize="sm" marginTop={3}>
            <FormattedMessage
              defaultMessage="We could not load delivery options. Please try again."
              id="pdp_delivery.error.load"
            />
          </Text>
        )}

        {shouldShowNoMethodsState && (
            <Box className="methods-empty-state" {...styles.methodsEmpty}>
              <Flex {...styles.methodsEmptyNotice}>
                <Box {...styles.methodsEmptyIcon} aria-hidden>
                  <PdpDeliveryMethodErrorIcon />
                </Box>
                <Text {...styles.methodsEmptyText}>
                  <FormattedMessage
                    defaultMessage="Currently unavailable in your area"
                    id="pdp_delivery.no_methods"
                  />
                </Text>
              </Flex>
            </Box>
          )}

        {!showMethodsSkeleton &&
          !pdpQuery.isError &&
          methods.map((method, index) => {
            const available = method.available !== false;
            const amount =
              method.price?.amount != null ?
                method.price.amount :
                method.actualPrice ?? 0;
            const isFree = amount === 0 || amount == null;

            return (
              <Flex
                className="method-row"
                key={method.id || index}
                {...styles.methodRow}
                opacity={available ? 1 : 0.55}
              >
                <Box {...styles.methodIcon}>
                  <Box {...styles.methodIconButton} aria-hidden>
                    {available ?
                      <PdpDeliveryMethodSuccessIcon /> :
                      <PdpDeliveryMethodErrorIcon />}
                  </Box>
                </Box>
                <Box {...styles.methodBody}>
                  <Text {...styles.methodName}>{method.name}</Text>
                  {(method.leadTime?.displayMessage || method.description) ? (
                    <Box {...styles.methodBodySecondary}>
                      {method.leadTime?.displayMessage ? (
                        <Text {...styles.methodLead}>
                          {`${method.leadTime.displayMessage}*`}
                        </Text>
                      ) : null}
                      {method.description ? (
                        <Text {...styles.methodDescription}>{method.description}</Text>
                      ) : null}
                    </Box>
                  ) : null}
                  {!available && method.unavailableReason ? (
                    <Text color="red.600" fontSize="sm" marginTop={1}>
                      {method.unavailableReason}
                    </Text>
                  ) : null}
                </Box>
                <Text {...styles.methodPrice}>
                  {isFree ?
                    intl.formatMessage({
                      defaultMessage: 'Free',
                      id: 'shipping_method.price.free',
                    }) :
                    (
                      formatCurrencyHideTrailingZeroCents(intl, amount, displayCurrency)
                    )}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>

      {showDeliveryTimingDisclaimer ? (
        <Text {...styles.disclaimer}>
          <FormattedMessage
            defaultMessage="* Delivery timing shown is based on one unit. Final shipping costs are calculated at checkout."
            id="pdp_delivery.disclaimer"
          />
        </Text>
      ) : null}
    </Box>
  );
}

PdpDeliveryAvailability.propTypes = {
  product: PropTypes.object.isRequired,
  tooltipText: PropTypes.string,
};

export default PdpDeliveryAvailability;
